import { useState, useEffect } from "react";
import { ChevronUp, Shield, Mail, ExternalLink } from "lucide-react";

const VideoInspectPrivacy = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="videoinspect-privacy-page">
      <main className="pp-main" data-testid="videoinspect-privacy-content">
        <article className="pp-article">
          <header className="pp-header" data-testid="videoinspect-privacy-header">
            <div className="pp-header-icon">
              <Shield size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Privacy Policy — Video Inspect</h1>
            <p className="pp-last-updated">Last updated: April 24, 2026</p>
          </header>

          <section className="pp-section" data-testid="section-vi-intro">
            <div className="pp-highlight-box">
              <p>
                Video Inspect ("the app," "we," "us") is built around a simple principle:{" "}
                <strong>your videos never leave your device</strong>. This policy explains
                exactly what that means and what limited data is involved when you make a
                purchase.
              </p>
            </div>
          </section>

          <section className="pp-section" data-testid="section-vi-on-device">
            <h2 className="pp-section-heading">
              Your Videos and Your Data Stay on Your Device
            </h2>
            <p>
              When you select a video to index and search, all processing happens entirely on
              your iPhone:
            </p>
            <ul className="pp-list">
              <li>
                Videos you select are never uploaded to us or anyone else.
              </li>
              <li>
                The visual analysis (indexing) that makes your videos searchable runs entirely
                on your device using on-device machine learning.
              </li>
              <li>
                Your search queries, search results, and the search index itself are stored
                only on your device.
              </li>
              <li>
                We have no servers that receive, store, or process your videos or their
                contents. We cannot see your videos, your searches, or your results.
              </li>
            </ul>
            <p>
              If you delete the app, the on-device data it created is removed with it.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-purchases">
            <h2 className="pp-section-heading">What Data Is Involved in Purchases</h2>
            <p>
              Video Inspect offers a one-time purchase to unlock unlimited indexing. Purchases
              are processed by Apple and by RevenueCat, Inc., our purchase-management provider.
              To process and validate your purchase, the following limited data is involved:
            </p>
            <ul className="pp-list">
              <li>
                <strong>Purchase information</strong> — whether you have purchased the unlock,
                and related transaction data needed to deliver and restore your purchase.
              </li>
              <li>
                <strong>A device identifier</strong> — your device's vendor identifier (IDFV),
                used to associate your purchase with your device so it can be restored.
              </li>
              <li>
                <strong>An anonymous purchase ID</strong> — generated by RevenueCat. We do not
                require or collect any account, name, email, or login.
              </li>
            </ul>
            <p>
              This purchase data does <strong>not</strong> include your videos, video frames,
              search queries, or any content from inside your videos. None of that ever leaves
              your device.
            </p>
            <p>
              RevenueCat processes this data on our behalf. You can review RevenueCat's privacy
              practices at{" "}
              <a
                href="https://www.revenuecat.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="vi-revenuecat-link"
              >
                revenuecat.com/privacy
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              . Apple processes payments under its own privacy policy.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-not-collect">
            <h2 className="pp-section-heading">What We Do NOT Collect</h2>
            <ul className="pp-list pp-list-cross">
              <li>
                We do not collect or upload your videos or their contents.
              </li>
              <li>
                We do not track you across apps or websites. We do not use advertising
                identifiers (IDFA).
              </li>
              <li>
                We do not use third-party analytics or advertising SDKs.
              </li>
              <li>
                We do not require an account, name, email, or login to use the app.
              </li>
            </ul>
          </section>

          <section className="pp-section" data-testid="section-vi-sharing">
            <h2 className="pp-section-heading">Data Sharing</h2>
            <p>
              We do not sell your data. The only data shared is the limited purchase data
              above, shared with Apple and RevenueCat solely to process and restore your
              purchase.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-children">
            <h2 className="pp-section-heading">Children's Privacy</h2>
            <p>
              Video Inspect is not directed at children under 13 and does not knowingly collect
              personal information from children.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-choices">
            <h2 className="pp-section-heading">Your Choices</h2>
            <p>
              Because your videos and searches stay on your device, you control them directly —
              deleting a video or the app removes its data. For questions about purchase data
              held by RevenueCat or Apple, you may contact us at the address below.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-changes">
            <h2 className="pp-section-heading">Changes to This Policy</h2>
            <p>
              We may update this policy as the app evolves. Material changes will be reflected
              by updating the "Last updated" date above.
            </p>
          </section>

          <section className="pp-section" data-testid="section-vi-contact">
            <h2 className="pp-section-heading">Contact</h2>
            <div className="pp-contact-card" data-testid="vi-contact-card">
              <p className="pp-contact-name">Radhesh Gowd</p>
              <a
                href="mailto:support@radships.com"
                className="pp-contact-email"
                data-testid="vi-contact-email"
              >
                <Mail size={16} />
                support@radships.com
              </a>
            </div>
          </section>

          <footer className="pp-footer" data-testid="videoinspect-privacy-footer">
            <p>&copy; {new Date().getFullYear()} Radhesh Gowd. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="vi-privacy-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default VideoInspectPrivacy;
